import { useState, useRef, useEffect } from "react";
import { Smile, Send, MoreVertical } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useChatSocket } from "@/hooks/useChatSocket";
import { useSearchParams } from "react-router-dom";
import {
  ChatMessageDTO,
  useGetMessagesQuery,
  useGetRoomDetailsQuery,
  useGetUserOnlineOfflineStatusQuery,
  useSendMessageMutation,
} from "@/redux/feature/chat/chatApi";
import { joinChatRoom, sendTypingStatus } from "@/services/chatSocketService";
import { useGlobalContext } from "@/context/GlobalContext";
import { TChatRoom } from "@/redux/feature/chat/chatType";
export type TChatMessage = {
  id: string;
  sender: "client" | "creator";
  senderName: string;
  content: string;
  timestamp: Date;
};

interface Contact {
  id: string;
  name: string;
  avatar?: string;
  isOnline?: boolean;
}
const formatTime = (date: Date) =>
  date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

interface ChatWindowProps {
  contact: Contact | null;
  // messages: TChatMessage[];
  isTyping?: boolean;
  onSendMessage: (content: string) => void;
  className?: string;
}

export function ChatWindow({ contact, className }: ChatWindowProps) {
  const { userData } = useGlobalContext();
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const myRole = userData?.role as "client" | "creator"; // for creator end => "creator"
  const otherRole = myRole === "client" ? "creator" : "client";

  const [isTyping, setIsTyping] = useState(false);
  const [searchParams] = useSearchParams();
  const roomId = searchParams.get("room");
  const [otherLastReadAtLive, setOtherLastReadAtLive] = useState<Date | null>(
    null,
  );
  const { socket, connected, userId } = useChatSocket(true);
  const [messages, setMessages] = useState<TChatMessage[]>([]);

  const { data: historyData } = useGetMessagesQuery(
    roomId ? { roomId, limit: 50, page: 1 } : (undefined as any),
    { skip: !roomId },
  );

  const [inputValue, setInputValue] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const chatRoomId = historyData?.data?.messages?.[0]?.chat_room_id;
  const { data: roomDto } = useGetRoomDetailsQuery(chatRoomId, {
    skip: !chatRoomId,
  });
  const room = roomDto?.data;

  const clientId = room?.client_id?._id;
  const creatorId = room?.creator_id?._id;

  const otherUserId =
    userId && clientId && creatorId
      ? userId === clientId
        ? creatorId
        : clientId
      : undefined;

  const otherLastReadAtISO =
    userId && clientId && creatorId
      ? userId === clientId
        ? room?.creator_last_read_at
        : room?.client_last_read_at
      : null;
  const otherLastReadAt = otherLastReadAtISO
    ? new Date(otherLastReadAtISO)
    : null;
  const { data: OnlineStatusDTO } = useGetUserOnlineOfflineStatusQuery(
    otherUserId,
    { skip: !otherUserId },
  );

  const onLineStatus = OnlineStatusDTO?.data?.online;

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  useEffect(() => {
    setOtherLastReadAtLive(otherLastReadAt);
  }, [otherLastReadAtISO]);

  useEffect(() => {
    if (!roomId) return;
    if (!connected) return;
    joinChatRoom(roomId);
  }, [roomId, connected]);

  // Load message history when available
  useEffect(() => {
    if (!historyData?.data?.messages) return;
    const mapped = historyData.data.messages.map((m: ChatMessageDTO) => {
      const senderId = (m.sender_id?._id ?? m.sender_id)?.toString?.() ?? "";
      return {
        id: m._id,
        senderName: m?.sender_id?.fullName || "",

        sender:
          senderId && userId && senderId === userId
            ? ("client" as const)
            : ("creator" as const),
        content: m.content,
        timestamp: new Date(m.timestamp),
      };
    });
    setMessages(mapped);
  }, [historyData, userId]);

  // Realtime receiveMessage
  useEffect(() => {
    if (!socket) return;
    if (!roomId) return;

    const onReceiveMessage = (m: any) => {
      const senderId = (m?.sender_id?._id ?? m?.sender_id)?.toString?.() ?? "";
      setMessages((prev) => [
        ...prev,
        {
          id: m?._id ?? Date.now().toString(),
          senderName: m?.sender_id?.fullName || "",
          sender:
            senderId && userId && senderId === userId ? "client" : "creator",
          content: m?.content ?? "",
          timestamp: new Date(m?.timestamp ?? Date.now()),
        },
      ]);
    };

    const onUserTyping = ({ isTyping }: any) => {
      setIsTyping(Boolean(isTyping));
    };

    const onMessageDeleted = ({ messageId, hard }: any) => {
      console.log("Delted_mesage", messageId);
      if (hard) {
        setMessages((prev) => prev.filter((m) => m.id !== messageId));
        return;
      }

      // optional fallback
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, content: "" } : m)),
      );
    };
    socket.on("receiveMessage", onReceiveMessage);
    socket.on("userTyping", onUserTyping);
    socket.on("messageDeleted", onMessageDeleted);

    return () => {
      socket.off("receiveMessage", onReceiveMessage);
      socket.off("userTyping", onUserTyping);
      socket.off("messageDeleted", onMessageDeleted);
    };
  }, [socket, userId]);

  useEffect(() => {
    if (!socket) return;

    const onRoomReadReceipt = ({ userId: readerId, readAt }: any) => {
      if (readerId === otherUserId) {
        setOtherLastReadAtLive(new Date(readAt));
      }
    };

    socket.on("roomReadReceipt", onRoomReadReceipt);
    return () => {
      socket.off("roomReadReceipt", onRoomReadReceipt);
    };
  }, [socket, otherUserId]);
  useEffect(() => {
    if (!socket) return;
    if (!connected) return;
    if (!roomId) return;
    if (!messages.length) return;

    const last = messages[messages.length - 1];

    if (last.sender === "client") {
      socket.emit("roomRead", { roomId });
    }
  }, [roomId, socket, connected, messages]);

  const [sendMessage, { isLoading: isSending }] = useSendMessageMutation();

  const handleSend = () => {
    if (!inputValue.trim()) return;
    if (!roomId) return;

    const content = inputValue;
    setInputValue("");

    // Stop typing
    sendTypingStatus(roomId, false);

    // Send via HTTP (backend emits receiveMessage via socket)
    sendMessage({
      receiver_id: otherUserId,
      content,
      message_type: "text",
    }).catch((e) => console.error("sendMessage failed:", e));
  };
  useEffect(() => {
    bottomRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  }, [messages, isTyping]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const getDeliveryStatus = (msg: TChatMessage) => {
    console.log("came here");
    if (msg.sender !== "client") {
      return null;
    }

    if (!otherLastReadAtLive) {
      return "Sent";
    }
    return otherLastReadAtLive.getTime() >= msg.timestamp.getTime()
      ? "Seen"
      : "Sent";
  };
  const lastOutgoingId = [...messages]
    .reverse()
    .find((m) => m.sender === "client")?.id;

  if (!contact) {
    return (
      <div
        className={cn(
          "flex flex-col items-center justify-center h-full bg-background",
          className,
        )}
      >
        <div className="text-center animate-fade-in">
          <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
            <Send className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium text-foreground mb-1">
            Select a conversation
          </h3>
          <p className="text-sm text-muted-foreground">
            Choose a contact to start messaging
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col h-full bg-background", className)}>
      {/* Header */}
      <header className="flex items-center justify-between px-6 h-16 border-b border-border bg-card animate-fade-in">
        <div className="flex items-center gap-3">
          <Avatar className="w-10 h-10">
            <AvatarImage src={contact.avatar} />
            <AvatarFallback className="bg-primary/10 text-primary font-medium">
              {contact.name
                .split(" ")
                .map((n) => n[0])
                .join("")}
            </AvatarFallback>
          </Avatar>
          <div>
            <h2 className="font-semibold text-foreground">{contact.name}</h2>
            <div className="flex items-center gap-1.5">
              {onLineStatus && <span className="online-indicator" />}
              <span className="text-xs text-muted-foreground">
                {onLineStatus ? "Online" : "Offline"}
              </span>
            </div>
          </div>
        </div>

        {/* <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="rounded-full">
            View Profile
          </Button>
          <Button variant="ghost" size="icon" className="rounded-full">
            <MoreVertical className="w-4 h-4" />
          </Button>
        </div> */}
      </header>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto scrollbar-thin px-4 py-4">
        <div className="space-y-4">
          {messages.map((message, index) => (
            <MessageBubble
              key={message.id}
              message={message}
              showSender={message.sender === "client"}
              style={{ animationDelay: `${index * 30}ms` }}
              deliveryStatus={getDeliveryStatus(message)}
            />
          ))}

          {isTyping && (
            <TypingIndicator
              name={contact.name.split(" ")[0]}
              avatar={contact.avatar}
            />
          )}
          <div ref={bottomRef} />
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Composer */}
      <div className="px-4 py-4 border-t border-border bg-card">
        <div className="flex items-center gap-3 bg-muted rounded-full px-4 py-2 transition-all duration-200 focus-within:ring-2 focus-within:ring-primary/20">
          <button className="p-1 hover:bg-muted-foreground/10 rounded-full transition-colors">
            <Smile className="w-5 h-5 text-muted-foreground" />
          </button>

          <input
            ref={inputRef}
            type="text"
            placeholder="Reply ..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
          />

          <button
            onClick={handleSend}
            disabled={!inputValue.trim()}
            className={cn(
              "w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200",
              inputValue.trim()
                ? "bg-primary text-primary-foreground hover:opacity-90 scale-100"
                : "bg-muted-foreground/20 text-muted-foreground scale-95",
            )}
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({
  message,
  showSender,
  style,
  deliveryStatus,
}: {
  message: TChatMessage;
  showSender?: boolean;
  style?: React.CSSProperties;
  deliveryStatus?: "Sent" | "Seen" | null;
}) {
  const isOutgoing = message.sender === "client";

  return (
    <div
      className={cn(
        "flex gap-3 w-full",
        isOutgoing
          ? "justify-end animate-slide-in-right"
          : "justify-start animate-slide-in-left",
      )}
      style={style}
    >
      {!isOutgoing && showSender && (
        <Avatar className="w-8 h-8 flex-shrink-0 mt-1">
          <AvatarFallback className="bg-muted text-muted-foreground text-xs">
            {message.senderName?.charAt(0) || "?"}
          </AvatarFallback>
        </Avatar>
      )}

      <div className={cn("max-w-[85%] space-y-1", isOutgoing && "items-end")}>
        {!isOutgoing && showSender && message.senderName && (
          <p className="text-xs font-medium text-muted-foreground px-1">
            {message.senderName}
          </p>
        )}

        <div
          className={cn(
            isOutgoing ? "message-bubble-outgoing" : "message-bubble-incoming",
          )}
        >
          <p className="text-sm leading-relaxed">{message.content}</p>
        </div>

        <p
          className={cn(
            "text-xs text-muted-foreground px-1",
            isOutgoing && "text-right",
          )}
        >
          {formatTime(message.timestamp)}
          {deliveryStatus ? ` • ${deliveryStatus}` : ""}
        </p>
      </div>
    </div>
  );
}

function TypingIndicator({ name, avatar }: { name: string; avatar?: string }) {
  return (
    <div className="flex gap-3 items-start animate-fade-in">
      <Avatar className="w-8 h-8">
        <AvatarImage src={avatar} />
        <AvatarFallback className="bg-muted text-muted-foreground text-xs">
          {name.charAt(0)}
        </AvatarFallback>
      </Avatar>

      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground px-1">{name}</p>
        <div className="message-bubble-incoming">
          <div className="flex gap-1 px-1 py-0.5">
            <span className="typing-dot" />
            <span className="typing-dot" />
            <span className="typing-dot" />
          </div>
        </div>
      </div>
    </div>
  );
}
